import type { Operator, QueryDefinition, QueryField, QueryRule, QueryValue } from './types';
import { isListOperator, isRangeOperator, isUnaryOperator } from './schema';

export type FilterMatch = 'all' | 'any';
export type FilterCondition = { field: string; operator: string; value: unknown };
export type FilterTree = { match: FilterMatch; rules: Array<FilterCondition | FilterTree> };
export type ConditionGroup = { id: string; match: FilterMatch; rules: QueryRule[] };
export type GroupedQuery = { match: FilterMatch; rules: Array<QueryRule | ConditionGroup> };
export const isConditionGroup = (rule: QueryRule | ConditionGroup): rule is ConditionGroup =>
  'rules' in rule;
export const emptyCondition = (): QueryRule => ({
  id: crypto.randomUUID(),
  join: 'AND',
  field: '',
  operator: '=',
  value: '',
});
export const emptyGroupedQuery = (): GroupedQuery => ({ match: 'all', rules: [] });

const wireOperators: Partial<Record<Operator, string>> = {
  '=': 'eq',
  '!=': 'not_in',
  in: 'in',
  notIn: 'not_in',
  '<': 'lt',
  '>': 'gt',
  '<=': 'lte',
  '>=': 'gte',
  contains: 'contains',
  beginsWith: 'starts_with',
  between: 'between',
  inLast: 'in_last',
  null: 'is_null',
  notNull: 'is_not_null',
};
export const booleanSignalFields = new Set([
  'has_verified_email',
  'has_verified_phone',
  'has_email',
  'has_phone',
  'has_linkedin',
  'is_persona',
  'card_on_file',
  'auto_reload_enabled',
  'pixel_installed',
  'linkedin_url',
  'description',
  'has_gaps',
]);

export function isCompleteCondition(rule: QueryRule, fields: QueryField[]): boolean {
  const field = fields.find(field => field.name === rule.field);
  if (!field || !field.operators.includes(rule.operator)) return false;
  if (rule.operator === 'inLast') {
    return (
      Array.isArray(rule.value) &&
      rule.value.length === 2 &&
      ['day', 'week', 'month'].includes(rule.value[1]) &&
      Number.isInteger(Number(rule.value[0])) &&
      Number(rule.value[0]) > 0
    );
  }
  if (isUnaryOperator(rule.operator)) return true;
  if (
    rule.value === null ||
    rule.value === '' ||
    (typeof rule.value === 'string' && !rule.value.trim())
  )
    return false;
  const values = Array.isArray(rule.value) ? rule.value : [rule.value];
  if (
    !values.length ||
    values.some(value => value === '' || (typeof value === 'string' && !value.trim()))
  )
    return false;
  if (isRangeOperator(rule.operator) && (!Array.isArray(rule.value) || values.length !== 2))
    return false;
  if (isListOperator(rule.operator) && !Array.isArray(rule.value)) return false;
  if (field.type === 'number' || field.type === 'duration') {
    if (values.some(value => !Number.isFinite(Number(value)))) return false;
    if (isRangeOperator(rule.operator) && Number(values[0]) > Number(values[1])) return false;
  }
  if (field.type === 'date' || field.type === 'datetime') {
    if (values.some(value => Number.isNaN(new Date(String(value)).getTime()))) return false;
    if (isRangeOperator(rule.operator) && new Date(String(values[0])) > new Date(String(values[1])))
      return false;
  }
  if (
    booleanSignalFields.has(rule.field) &&
    ![true, false, 'true', 'false'].includes(rule.value as string | boolean)
  )
    return false;
  return true;
}

export function validGroupedQuery(query: GroupedQuery, fields: QueryField[]): GroupedQuery {
  return {
    ...query,
    rules: query.rules.flatMap<QueryRule | ConditionGroup>(node => {
      if (!isConditionGroup(node))
        return isCompleteCondition(node, fields) ? [{ ...node, children: undefined }] : [];
      const rules = node.rules
        .filter(rule => isCompleteCondition(rule, fields))
        .map(rule => ({ ...rule, children: undefined }));
      return rules.length ? [{ ...node, rules }] : [];
    }),
  };
}

export function groupedQueryToFilter(query: GroupedQuery, fields: QueryField[]): FilterTree {
  const valid = validGroupedQuery(query, fields);
  const condition = (rule: QueryRule): FilterCondition | FilterTree => {
    const field = fields.find(field => field.name === rule.field)!;
    let value: unknown = rule.value;
    if (rule.operator === 'inLast' && Array.isArray(rule.value))
      value = { unit: rule.value[1], value: Number(rule.value[0]) };
    let operator = wireOperators[rule.operator];
    if (!operator) throw new Error(`Unsupported operator: ${rule.operator}`);
    if (
      (field.type === 'datetime' || field.type === 'date') &&
      rule.operator !== 'inLast' &&
      !isUnaryOperator(rule.operator)
    ) {
      const formatValue = (input: unknown) => {
        const text = String(input);
        if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
        if (field.type === 'date') {
          const dateOnly = text.match(/^(\d{4}-\d{2}-\d{2})/);
          if (dateOnly) return dateOnly[1];
          const date = new Date(text);
          if (!Number.isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return text;
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return `${text}:00Z`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(text)) return `${text}Z`;
        return new Date(text).toISOString();
      };
      value = Array.isArray(value) ? value.map(formatValue) : formatValue(value);
    }
    if (field.type === 'number' || field.type === 'duration')
      value = Array.isArray(value) ? value.map(Number) : Number(value);
    if (booleanSignalFields.has(field.apiField ?? rule.field)) {
      value = value === true || value === 'true';
      if (rule.operator === '!=') value = !value;
      operator = 'eq';
    } else if (rule.operator === '!=') value = Array.isArray(value) ? value : [value];
    return { field: field.apiField ?? rule.field, operator, value };
  };
  return {
    match: valid.match,
    rules: valid.rules.map(node =>
      isConditionGroup(node)
        ? { match: node.match, rules: node.rules.map(condition) }
        : condition(node)
    ),
  };
}

export const groupedConditions = (query: GroupedQuery): QueryRule[] =>
  query.rules.flatMap(node => (isConditionGroup(node) ? node.rules : [node]));
export const asResolutionQuery = (query: GroupedQuery): QueryDefinition => ({
  groups: [{ id: 'resolution', join: 'AND', rules: groupedConditions(query) }],
});
export const updateGroupedValue = (
  query: GroupedQuery,
  id: string,
  value: QueryValue
): GroupedQuery => ({
  ...query,
  rules: query.rules.map(node =>
    isConditionGroup(node)
      ? { ...node, rules: node.rules.map(rule => (rule.id === id ? { ...rule, value } : rule)) }
      : node.id === id
        ? { ...node, value }
        : node
  ),
});
export function removeGroupedRule(query: GroupedQuery, id: string): GroupedQuery {
  const rules = query.rules
    .filter(node => node.id !== id)
    .map(node =>
      isConditionGroup(node) ? { ...node, rules: node.rules.filter(rule => rule.id !== id) } : node
    )
    .filter(node => !isConditionGroup(node) || node.rules.length);
  return { ...query, rules };
}

type CompactCondition = { f: string; o: Operator; v: QueryValue };
type CompactGroup = { m: FilterMatch; r: CompactCondition[] };
type CompactQuery = { v: 3; m: FilterMatch; r: Array<CompactCondition | CompactGroup> };

const isCompactGroup = (item: CompactCondition | CompactGroup): item is CompactGroup => 'r' in item;
const base64Alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const utf8ToBinary = (text: string) => unescape(encodeURIComponent(text));
const binaryToUtf8 = (binary: string) => decodeURIComponent(escape(binary));
const encodeBase64 = (binary: string) => {
  let output = '';
  for (let index = 0; index < binary.length; index += 3) {
    const byte1 = binary.charCodeAt(index);
    const byte2 = index + 1 < binary.length ? binary.charCodeAt(index + 1) : 0;
    const byte3 = index + 2 < binary.length ? binary.charCodeAt(index + 2) : 0;
    const chunk = (byte1 << 16) | (byte2 << 8) | byte3;
    output += base64Alphabet[(chunk >> 18) & 63];
    output += base64Alphabet[(chunk >> 12) & 63];
    output += index + 1 < binary.length ? base64Alphabet[(chunk >> 6) & 63] : '=';
    output += index + 2 < binary.length ? base64Alphabet[chunk & 63] : '=';
  }
  return output;
};
const decodeBase64 = (base64: string) => {
  let output = '';
  for (let index = 0; index < base64.length; index += 4) {
    const char1 = base64[index];
    const char2 = base64[index + 1];
    const char3 = base64[index + 2];
    const char4 = base64[index + 3];
    const chunk =
      (base64Alphabet.indexOf(char1) << 18) |
      (base64Alphabet.indexOf(char2) << 12) |
      ((char3 === '=' ? 0 : base64Alphabet.indexOf(char3)) << 6) |
      (char4 === '=' ? 0 : base64Alphabet.indexOf(char4));
    output += String.fromCharCode((chunk >> 16) & 255);
    if (char3 !== '=') output += String.fromCharCode((chunk >> 8) & 255);
    if (char4 !== '=') output += String.fromCharCode(chunk & 255);
  }
  return output;
};
const encodeBase64Url = (text: string) =>
  encodeBase64(utf8ToBinary(text)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const decodeBase64Url = (text: string) => {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  return binaryToUtf8(decodeBase64(padded));
};
const compactCondition = (rule: QueryRule): CompactCondition => ({
  f: rule.field,
  o: rule.operator,
  v: rule.value,
});
export function serializeGroupedQuery(query: GroupedQuery): string | null {
  if (!query.rules.length) return null;
  const compact: CompactQuery = {
    v: 3,
    m: query.match,
    r: query.rules.map(node =>
      isConditionGroup(node)
        ? { m: node.match, r: node.rules.map(compactCondition) }
        : compactCondition(node)
    ),
  };
  return `g:${encodeBase64Url(JSON.stringify(compact))}`;
}

// Read compact shareable grouped-filter URLs written as `g:<base64url-json>`.
export function parseGroupedQuery(raw: string | null): GroupedQuery {
  if (!raw?.startsWith('g:')) return emptyGroupedQuery();
  try {
    const data = JSON.parse(decodeBase64Url(raw.slice(2))) as CompactQuery;
    if (data.v !== 3 || !['all', 'any'].includes(data.m) || !Array.isArray(data.r))
      return emptyGroupedQuery();
    const readCompactCondition = (item: CompactCondition): QueryRule => {
      const operator = item.o;
      if (!(operator in wireOperators)) throw new Error('Invalid operator');
      const value = item.v;
      if (
        value !== null &&
        !['string', 'number', 'boolean'].includes(typeof value) &&
        !(Array.isArray(value) && value.every(v => typeof v === 'string' || typeof v === 'number'))
      )
        throw new Error('Invalid value');
      return {
        id: crypto.randomUUID(),
        join: 'AND',
        field: item.f,
        operator,
        value: Array.isArray(value) ? value.map(String) : value,
      };
    };
    return {
      match: data.m,
      rules: data.r.map(item =>
        isCompactGroup(item)
          ? { id: crypto.randomUUID(), match: item.m, rules: item.r.map(readCompactCondition) }
          : readCompactCondition(item)
      ),
    };
  } catch {
    return emptyGroupedQuery();
  }
}

export function withSearchFilter(filter: FilterTree, search?: string): FilterTree {
  if (!search?.trim()) return filter;
  const condition = { field: 'search', operator: 'eq', value: search.trim() };
  if (!filter.rules.length) return { match: 'all', rules: [condition] };
  return filter.match === 'all'
    ? { ...filter, rules: [...filter.rules, condition] }
    : { match: 'all', rules: [filter, condition] };
}
